// ItemPriceUpdatePage.jsx
// The Item Creation screen with only the price editable: pick an item from the list, change its
// standard price and save. Every other field is shown read-only, and the server rejects the
// request if any of them differs from what is stored.
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import useAppSettings, { hasDecimals } from "./useAppSettings";

const EMPTY_FORM = {
  itemName: "",
  unitName: "",
  standardPrice: "",
  barcode: "",
  itemCode: "",
  taxRate: "",
  hsnCode: "",
};

const blank = (v) => (v === null || v === undefined ? "" : v);

async function errorText(res) {
  try {
    const data = await res.json();
    return data?.errorMessage || data?.message || `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

const ItemPriceUpdatePage = () => {
  const { t } = useTranslation();
  const tenancyId = localStorage.getItem("tenancyId");

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(null); // the item as loaded from the list
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { settings } = useAppSettings();
  const priceDecimals = settings.itemPriceUpdatePriceDecimals;

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, rowsPerPage]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/${tenancyId}/items-search?query=${encodeURIComponent(searchQuery)}&page=${page}&size=${rowsPerPage}&sort=itemName,asc`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` } }
      );
      if (!response.ok) {
        setItems([]);
        setTotalItems(0);
        return;
      }
      const data = await response.json();
      setItems(data.content || []);
      setTotalItems(data.totalElements || 0);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePick = (item) => {
    setSelected(item);
    setFormData({
      itemName: blank(item.itemName),
      unitName: blank(item.unitName),
      standardPrice: blank(item.standardPrice),
      barcode: blank(item.barcode),
      itemCode: blank(item.itemCode),
      taxRate: blank(item.taxRate),
      hsnCode: blank(item.hsnCode),
    });
    setErrorMessage(null);
    setSuccessMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setSelected(null);
    setFormData(EMPTY_FORM);
    setErrorMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;

    const price = Number(formData.standardPrice);
    if (formData.standardPrice === "" || !Number.isFinite(price) || price <= 0) {
      setErrorMessage(t("positivePrice"));
      return;
    }
    if (!priceDecimals && hasDecimals(price)) {
      setErrorMessage("Decimal prices are turned off in Settings. Enter a whole number.");
      return;
    }
    if (Math.round(price * 100) / 100 !== price) {
      setErrorMessage("Price can have at most 2 decimal places.");
      return;
    }

    // Send the item as it was loaded with only the price changed; the server refuses the
    // request if any other field no longer matches what is stored.
    const body = {
      itemName: selected.itemName,
      unitName: selected.unitName,
      barcode: selected.barcode,
      itemCode: selected.itemCode,
      taxRate: selected.taxRate,
      hsnCode: selected.hsnCode,
      standardPrice: price,
    };

    setSaving(true);
    setErrorMessage(null);
    try {
      const response = await fetch(`/api/${tenancyId}/pricing/item-master-price/${selected.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        setErrorMessage(await errorText(response));
        return;
      }
      setSuccessMessage(`Price of ${selected.itemName} updated to ${price.toFixed(2)}.`);
      setSelected(null);
      setFormData(EMPTY_FORM);
      fetchItems();
    } catch (error) {
      console.error("Error updating price:", error);
      setErrorMessage(t("updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const readOnly = { readOnly: true };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" align="center" gutterBottom>
        Item Price Update
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Search Items"
              placeholder="Type to search by name or code"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              variant="outlined"
              sx={{ my: 3 }}
            />
            {!selected && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Click the pencil icon on an item in the list below to change its price. Only the price can be changed here.
              </Alert>
            )}
            <TextField
              fullWidth
              label={t("itemName")}
              value={formData.itemName}
              InputProps={readOnly}
              disabled={!selected}
              variant="outlined"
              sx={{ mb: 3 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label={t("unitName")} value={formData.unitName} InputProps={readOnly} disabled={!selected} />
          </Grid>

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label={t("standardPrice")}
              name="standardPrice"
              type="number"
              value={formData.standardPrice}
              onChange={(e) => setFormData((f) => ({ ...f, standardPrice: e.target.value }))}
              inputProps={{ min: "0", step: priceDecimals ? "0.01" : "any" }}
              disabled={!selected}
              helperText={
                selected
                  ? priceDecimals
                    ? "The only field you can change on this screen."
                    : "The only field you can change on this screen. Whole numbers only (decimals are off in Settings)."
                  : ""
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label={t("barcode")} value={formData.barcode} InputProps={readOnly} disabled={!selected} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label={t("itemCode")} value={formData.itemCode} InputProps={readOnly} disabled={!selected} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label={t("taxRate")} value={formData.taxRate} InputProps={readOnly} disabled={!selected} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label={t("hsnCode")} value={formData.hsnCode} InputProps={readOnly} disabled={!selected} />
          </Grid>
          <Grid item xs={12} sm={selected ? 8 : 12}>
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={!selected || saving}>
              {saving ? <CircularProgress size={24} color="inherit" /> : "Update Price"}
            </Button>
          </Grid>
          {selected && (
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" fullWidth onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
            </Grid>
          )}
        </Grid>
      </form>

      <Typography variant="h5" align="center" gutterBottom sx={{ mt: 5 }}>
        {t("listOfItems")}
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("itemName")}</TableCell>
                <TableCell>{t("unitName")}</TableCell>
                <TableCell align="right">{t("standardPrice")}</TableCell>
                <TableCell>{t("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} selected={selected?.id === item.id}>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell>{item.unitName}</TableCell>
                  <TableCell align="right">
                    {item.standardPrice === null || item.standardPrice === undefined
                      ? ""
                      : Number(item.standardPrice).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handlePick(item)} aria-label="Change price">
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}
    </Container>
  );
};

export default ItemPriceUpdatePage;
